import $ from 'jquery';
import h from 'hyperscript';
import listIcon from '@disy/cadenza-icons/list.svg';

import { createIconButton } from 'ui/button/button';
import { Select } from 'ui/select/select';

import { type EventKey, on, unByKey } from 'cadenza/utils/event-util';
import { logger } from 'cadenza/utils/logging';
import { icon } from 'cadenza/utils/icon/icon';
import { context } from 'cadenza/map/context';
import { LayerProperty } from 'cadenza/map/layer-property';
import { adaptAvailableStyles } from 'cadenza/map/layer-configuration-panel/webmapstyle-configuration/webmapstyle-configuration-utils';

import type ImageWMSSource from 'ol/source/ImageWMS';
import type ImageLayer from 'ol/layer/Image';
import type OlMap from 'ol/Map';

import './webmapstyle-configuration.css';
import i18n from './webmapstyle-configuration.properties';

const COMPONENT_NAME = 'd-webmap-style-config';

/**
 * Unlike a selected web map style which consists of only a string id,
 * this web map style info consists of all the required information to display available styles,
 * both as text and as a legend.
 */
export interface WebMapStyleDto {
  id: string;
  legendUrl: string;
  name: string;
}

export class WebMapStyleConfiguration extends HTMLElement {

  _selectedStyle: string;
  _map: OlMap;
  _layer: ImageLayer<ImageWMSSource>;

  _changeEventKey?: EventKey;

  // initialized in connectedCallback
  _styleSelect!: Select;
  // initialized in connectedCallback
  _styleDialogButton!: HTMLButtonElement;

  constructor (selectedStyle: string, map: OlMap, layerId: string) {
    super();
    this._selectedStyle = selectedStyle;
    this._map = map;
    this._layer = context(this._map).getLayer(layerId);
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);
    this._render();
  }

  disconnectedCallback () {
    unByKey(this._changeEventKey);
  }

  get state (): { selectedStyle: string } {
    return {
      selectedStyle: this._selectedStyle
    };
  }

  _render () {
    const availableStyles: WebMapStyleDto[] = this._layer.get(LayerProperty.AVAILABLE_STYLES);
    this.innerHTML = '';
    this._styleDialogButton = createIconButton(
      icon(listIcon),
      i18n('styleDialogButton'),
      { onclick: () => this._openDialog(availableStyles) });
    this._styleSelect = this._createStyleSelect(availableStyles);
    this.append(
      h('form-group', [
        h('label', i18n('style')),
        this._styleSelect
      ])
    );
  }

  _createStyleSelect (availableStyles: WebMapStyleDto[]): Select {
    const select = new Select({
      data: adaptAvailableStyles(availableStyles, this._selectedStyle),
      elementRight: this._styleDialogButton
    });
    this._changeEventKey = on(select, 'change', () => {
      this._selectedStyle = select.selection[0].id;
      this._updateLayer();
    });
    return select;
  }

  _updateLayer () {
    // this will likely not be sufficient if the layer is a group layer with multiple WMS layers
    this._layer.getSource()!.updateParams({ STYLES: this._selectedStyle });
    const legendUrl = new URL(this._layer.get(LayerProperty.LEGEND_URL));
    legendUrl.searchParams.set('style', this._selectedStyle);
    this._layer.set(LayerProperty.LEGEND_URL, legendUrl.toString());
  }

  _openDialog (availableStyles: WebMapStyleDto[]) {
    import(/* webpackChunkName: "webmapstyle-configuration-dialog" */ 'cadenza/map/layer-configuration-panel/webmapstyle-configuration/webmapstyle-configuration-dialog')
      .then(({ WebMapStyleConfigurationDialog }) => new WebMapStyleConfigurationDialog(this._selectedStyle, availableStyles).connectAndShow())
      .then(selectedStyle => {
        if (!selectedStyle) {
          return;
        }
        this._styleSelect.selection = selectedStyle;
        $(this._styleSelect.accessibleSelect).trigger('change');
      })
      .catch(error => {
        logger.error('Failed to select a style in the dialog.', error);
      });
  }

}

customElements.define(COMPONENT_NAME, WebMapStyleConfiguration);
